import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import LinearProgress from '@mui/material/LinearProgress';
import PaymentsIcon from '@mui/icons-material/Payments';
import PersonIcon from '@mui/icons-material/Person';
import EngineeringIcon from '@mui/icons-material/Engineering';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import StatCard from './StatCard';
import CategoryIcon from './CategoryIcon';
import { formatMoney } from './Money';
import { categoryName } from '../i18n';
import { chartColors } from '../theme';

/** ₹ amounts on the y-axis stay compact: 85000 → ₹85k, 1200000 → ₹12L */
const compactMoney = (v) => {
  if (v >= 100000) return `₹${(v / 100000).toFixed(v % 100000 ? 1 : 0)}L`;
  if (v >= 1000) return `₹${Math.round(v / 1000)}k`;
  return `₹${v}`;
};

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Card sx={{ px: 1.5, py: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="subtitle2">{formatMoney(payload[0].value)}</Typography>
    </Card>
  );
};

/** Horizontal single-hue bars with direct labels — magnitude by category */
const CategoryBars = ({ items, color }) => {
  const max = Math.max(...items.map((c) => c.total), 1);
  return (
    <Stack spacing={1.5}>
      {items.map((cat) => (
        <Box key={cat.key}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <CategoryIcon icon={cat.icon} sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" noWrap>
                {categoryName(cat)}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0 }}>
              {formatMoney(cat.total)}
            </Typography>
          </Stack>
          <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}>
            <Box
              sx={{
                width: `${Math.max(2, (cat.total / max) * 100)}%`,
                height: '100%',
                borderRadius: 4,
                bgcolor: color,
              }}
            />
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

const OverviewTab = ({ summary }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = chartColors[theme.palette.mode];

  const { totals, budget, budgetUsedPct, byCategory, spendByDay, workerDues } = summary;
  const hasSpend = totals.totalSpent > 0;

  const trendData = spendByDay.map((d) => ({
    date: d.date.slice(5).replace('-', '/'),
    total: d.total,
  }));

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5}>
        <StatCard label={t('project.byConstructor')} value={formatMoney(totals.constructorPaid)} icon={<PaymentsIcon />} />
        <StatCard label={t('project.byClient')} value={formatMoney(totals.clientPaid)} icon={<PersonIcon />} color="secondary.main" />
        <StatCard label={t('project.wages')} value={formatMoney(totals.wagesPaid)} icon={<EngineeringIcon />} color="success.main" />
      </Stack>

      {budget && (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">
                {t('project.budgetUsed', { pct: budgetUsedPct })}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {formatMoney(totals.totalSpent)} / {formatMoney(budget)}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, budgetUsedPct)}
              color={budgetUsedPct > 90 ? 'error' : 'primary'}
              sx={{ height: 10, borderRadius: 5 }}
            />
          </CardContent>
        </Card>
      )}

      {!hasSpend ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary">{t('project.noSpend')}</Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {t('project.spendByCategory')}
              </Typography>
              <CategoryBars items={byCategory} color={colors.spend} />
            </CardContent>
          </Card>

          {trendData.length > 1 && (
            <Card>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {t('project.spendTrend')}
                </Typography>
                <Box sx={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer>
                    <AreaChart data={trendData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="spendFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={colors.trend} stopOpacity={0.25} />
                          <stop offset="100%" stopColor={colors.trend} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={compactMoney}
                        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                        axisLine={false}
                        tickLine={false}
                        width={48}
                      />
                      <Tooltip content={<TrendTooltip />} cursor={{ stroke: theme.palette.divider }} />
                      <Area
                        type="monotone"
                        dataKey="total"
                        stroke={colors.trend}
                        strokeWidth={2}
                        fill="url(#spendFill)"
                        activeDot={{ r: 4 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {workerDues && workerDues.balance !== 0 && (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <EngineeringIcon color="warning" />
                <Typography variant="subtitle2">
                  {t('worker.balance')} ({t('project.wages')})
                </Typography>
              </Stack>
              <Typography variant="h6" color={workerDues.balance > 0 ? 'error.main' : 'success.main'}>
                {formatMoney(Math.abs(workerDues.balance))}{' '}
                <Typography component="span" variant="caption" color="text.secondary">
                  {workerDues.balance > 0 ? t('worker.toReceive') : t('worker.advanceHeld')}
                </Typography>
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default OverviewTab;
